import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Project } from '@/types/api';

export type ProjectPayload = Omit<Project, 'id'>;

const KEY = ['projects'] as const;
const PUBLIC_KEY = ['public-projects'] as const;

/** Published projects for the public site. */
export function usePublicProjects() {
  return useQuery({
    queryKey: PUBLIC_KEY,
    queryFn: () => api.get<Project[]>('/projects'),
    staleTime: 10 * 60_000,
  });
}

/** One published project by slug, for its detail page. */
export function usePublicProject(slug: string | undefined) {
  return useQuery({
    queryKey: [...PUBLIC_KEY, slug],
    queryFn: () => api.get<Project>(`/projects/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useProjects() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<Project[]>('/admin/projects') });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: PUBLIC_KEY });
  };
}

export function useSaveProject() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<ProjectPayload> }) =>
      id
        ? api.patch<Project>(`/admin/projects/${id}`, values)
        : api.post<Project>('/admin/projects', values),
    onSuccess: invalidate,
  });
}

export function useDeleteProject() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/projects/${id}`),
    onSuccess: invalidate,
  });
}

export function useReorderProjects() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (items: Array<{ id: string; sortOrder: number }>) =>
      api.put('/admin/projects/reorder', { items }),
    onSuccess: invalidate,
  });
}
